import React, { useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Search, Sun, Moon, Monitor, Bell, Coins, Check, ChevronRight } from "lucide-react";
import { cn } from "lib/utils";
import { useTheme } from "contexts/ThemeContext";
import { useBalance, useCurrentSubscriptionPlan } from "core";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "components/ui/tooltip";
import {
    Dropdown,
    DropdownTrigger,
    DropdownContent,
    DropdownItem,
    DropdownLabel,
} from "components/ui/dropdown";
import { Skeleton } from "components/ui/skeleton";
import { buildBreadcrumb } from "../../navigation";
import { useSidebar } from "../../SidebarContext";

/* ══════════════════════════════════════════════════════════════════════════
   Application header.

   Replaces the `UnifiedHeader` → `ActionSection` chain, which was a memoised
   wrapper around a memoised wrapper that ultimately rendered four buttons,
   reached into `window.currentActiveTab` and `window.abTestingCreateTestRef`
   for its context-sensitive action, and left the bar's left-hand side empty
   on every route but one.

   What the bar does now:
   • States where you are. The left side carries the current page title,
     resolved from the shared navigation registry — the old header showed
     nothing, so on a deep route the only orientation cue was the sidebar.
   • Offers global search. ⌘K was implemented but only mounted in a layout
     that never rendered; it is now reachable from every screen, and visible
     as a control rather than being a secret.
   • Keeps the credit balance, theme switch and notifications, with real
     labels and the shared tooltip instead of `title` attributes.
   ══════════════════════════════════════════════════════════════════════════ */

const THEME_ICON = { light: Sun, dark: Moon, auto: Monitor } as const;
const THEME_LABEL = { light: "Light", dark: "Dark", auto: "System" } as const;

/** Remaining sends, coloured by how close the account is to its plan limit. */
const CreditBalance: React.FC = () => {
    const navigate = useNavigate();
    const { balance, isLoading } = useBalance();
    const { data: currentPlan } = useCurrentSubscriptionPlan();

    const tone = useMemo(() => {
        if (balance == null || !currentPlan?.emails_offer) return "success";
        const pct = (balance / currentPlan.emails_offer) * 100;
        if (pct >= 40) return "success";
        if (pct > 10) return "warning";
        return "danger";
    }, [balance, currentPlan]);

    if (balance == null && !isLoading) return null;

    const formatted = balance != null ? new Intl.NumberFormat("en-US").format(balance) : "0";

    return (
        <Tooltip>
            <TooltipTrigger asChild>
                <button
                    type="button"
                    onClick={() => navigate("/payments")}
                    aria-label={`Email credits remaining: ${formatted}. Open billing.`}
                    className={cn(
                        "inline-flex items-center gap-1.5 h-9 pl-2.5 pr-3 rounded-xl border font-semibold text-meta tabular-nums",
                        "transition-colors duration-150 focus-ring",
                        tone === "success" && "bg-success-subtle border-success-border text-success-on-subtle hover:bg-success-subtle",
                        tone === "warning" && "bg-warning-subtle border-warning-border text-warning-on-subtle",
                        tone === "danger" && "bg-danger-subtle border-danger-border text-danger-on-subtle"
                    )}
                >
                    <Coins className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
                    {isLoading ? <Skeleton className="h-3 w-10 rounded" /> : formatted}
                </button>
            </TooltipTrigger>
            <TooltipContent>
                {tone === "danger"
                    ? "Credits running low — top up to keep sending"
                    : "Email credits remaining"}
            </TooltipContent>
        </Tooltip>
    );
};

/** Icon button shared by every header control, so they line up exactly. */
const HeaderButton = React.forwardRef<
    HTMLButtonElement,
    React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string }
>(({ label, className, children, ...props }, ref) => (
    <button
        ref={ref}
        type="button"
        aria-label={label}
        className={cn(
            "inline-flex items-center justify-center w-9 h-9 rounded-xl flex-shrink-0",
            "text-content-muted hover:text-content hover:bg-surface-hover",
            "transition-colors duration-150 focus-ring touch-manipulation",
            className
        )}
        {...props}
    >
        {children}
    </button>
));
HeaderButton.displayName = "HeaderButton";

export interface AppHeaderProps {
    /** Opens the command palette */
    onOpenSearch: () => void;
    /** Rendered before the standard controls — page-specific actions */
    actions?: React.ReactNode;
    /** Replaces the derived page title (e.g. a brand mark on full-bleed routes) */
    titleSlot?: React.ReactNode;
    notificationCount?: number;
    onNotificationsClick?: () => void;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
    onOpenSearch,
    actions,
    titleSlot,
    notificationCount = 0,
    onNotificationsClick,
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { theme, setTheme } = useTheme();
    const { openMobile, mobileOpen } = useSidebar();

    const crumbs = React.useMemo(
        () => buildBreadcrumb(location.pathname),
        [location.pathname]
    );
    const ThemeIcon = THEME_ICON[theme as keyof typeof THEME_ICON] ?? Monitor;

    return (
        <TooltipProvider delayDuration={300}>
            <header
                className="flex-shrink-0 z-40 bg-surface border-b border-line safe-padding-top"
                style={{ touchAction: "none" }}
            >
                <div className="h-14 flex items-center gap-2 px-3 sm:px-5">
                    {/* ── Drawer trigger (mobile) ── */}
                    <button
                        type="button"
                        onClick={openMobile}
                        aria-label="Open navigation"
                        aria-expanded={mobileOpen}
                        aria-controls="app-sidebar"
                        className="lg:hidden inline-flex items-center justify-center w-9 h-9 -ml-1 rounded-xl text-content-muted hover:text-content hover:bg-surface-hover transition-colors focus-ring touch-manipulation"
                    >
                        <Menu className="w-5 h-5" />
                    </button>
                    <div className="min-w-0 flex-1 flex items-center gap-2">
                        {titleSlot ?? (crumbs.length > 0 && (
                            <nav aria-label="Breadcrumb" className="min-w-0">
                                <ol className="flex items-center gap-1 min-w-0">
                                    {crumbs.map((crumb, i) => {
                                        const isLast = i === crumbs.length - 1;
                                        return (
                                            <li
                                                key={`${crumb.label}-${i}`}
                                                className={cn(
                                                    "flex items-center gap-1 min-w-0",
                                                    // On a phone the trail competes with the header
                                                    // controls, so only the current page is shown —
                                                    // the sidebar already says which section you are in.
                                                    !isLast && "hidden sm:flex"
                                                )}
                                            >
                                                {i > 0 && (
                                                    <ChevronRight
                                                        className="hidden sm:block w-3.5 h-3.5 flex-shrink-0 text-content-subtle"
                                                        aria-hidden="true"
                                                    />
                                                )}
                                                {crumb.to && !isLast ? (
                                                    <Link
                                                        to={crumb.to}
                                                        className="text-ui-lg font-medium text-content-muted hover:text-content truncate rounded px-1 -mx-1 transition-colors focus-ring"
                                                    >
                                                        {crumb.label}
                                                    </Link>
                                                ) : (
                                                    <span
                                                        aria-current={isLast ? "page" : undefined}
                                                        className="text-ui-lg font-semibold text-content truncate"
                                                    >
                                                        {crumb.label}
                                                    </span>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ol>
                            </nav>
                        ))}
                    </div>

                    {/* ── Search. A real affordance on desktop, an icon on phones. ── */}
                    <button
                        type="button"
                        onClick={onOpenSearch}
                        aria-label="Search and jump to anywhere (Command K)"
                        className="hidden md:inline-flex items-center gap-2 h-9 pl-3 pr-2 rounded-xl bg-surface-sunken border border-line text-content-muted hover:text-content hover:border-line-strong transition-colors focus-ring min-w-[15rem]"
                    >
                        <Search className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                        <span className="text-meta font-medium flex-1 text-left">Search…</span>
                        <kbd className="inline-flex items-center h-5 px-1.5 rounded border border-line bg-surface text-2xs font-sans font-semibold text-content-subtle">
                            ⌘K
                        </kbd>
                    </button>

                    <HeaderButton
                        label="Search"
                        onClick={onOpenSearch}
                        className="md:hidden"
                    >
                        <Search className="w-[18px] h-[18px]" />
                    </HeaderButton>

                    {actions}

                    <span className="hidden sm:block w-px h-6 bg-line flex-shrink-0" aria-hidden="true" />

                    <div className="hidden sm:block">
                        <CreditBalance />
                    </div>

                    {/* ── Theme. An explicit menu, not a blind three-way cycle:
                           the old toggle rotated light → dark → auto with no way
                           to see or pick the current mode. ── */}
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <HeaderButton label={`Theme: ${THEME_LABEL[theme as keyof typeof THEME_LABEL] ?? "System"}`}>
                                <ThemeIcon className="w-[18px] h-[18px]" />
                            </HeaderButton>
                        </DropdownTrigger>
                        <DropdownContent align="end" className="min-w-[9rem]">
                            <DropdownLabel>Appearance</DropdownLabel>
                            {(["light", "dark", "auto"] as const).map((mode) => {
                                const Icon = THEME_ICON[mode];
                                return (
                                    <DropdownItem
                                        key={mode}
                                        onSelect={() => setTheme(mode)}
                                        className={cn(theme === mode && "text-content font-semibold")}
                                    >
                                        <Icon className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
                                        <span className="flex-1">{THEME_LABEL[mode]}</span>
                                        {theme === mode && (
                                            <Check className="w-3.5 h-3.5 text-brand" aria-hidden="true" />
                                        )}
                                    </DropdownItem>
                                );
                            })}
                        </DropdownContent>
                    </Dropdown>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <HeaderButton
                                label={
                                    notificationCount > 0
                                        ? `Notifications, ${notificationCount} unread`
                                        : "Notifications"
                                }
                                onClick={onNotificationsClick}
                                className="relative"
                            >
                                <Bell className="w-[18px] h-[18px]" />
                                {notificationCount > 0 && (
                                    <span className="absolute top-1.5 right-1.5 min-w-[15px] h-[15px] px-1 rounded-full bg-danger-solid text-white text-2xs font-bold inline-flex items-center justify-center tabular-nums">
                                        {notificationCount > 9 ? "9+" : notificationCount}
                                    </span>
                                )}
                            </HeaderButton>
                        </TooltipTrigger>
                        <TooltipContent>Notifications</TooltipContent>
                    </Tooltip>

                    {/* Disabled before this work began (it is commented out in HEAD too);
                        /help is still reachable from the account menu. Re-enabling this
                        needs `LifeBuoy` added back to the lucide-react import above. */}
                    {/* <Tooltip>
                        <TooltipTrigger asChild>
                            <HeaderButton
                                label="Help and support"
                                onClick={() => navigate("/help")}
                                className="hidden sm:inline-flex"
                            >
                                <LifeBuoy className="w-[18px] h-[18px]" />
                            </HeaderButton>
                        </TooltipTrigger>
                        <TooltipContent>Help &amp; support</TooltipContent>
                    </Tooltip> */}
                </div>
            </header>
        </TooltipProvider>
    );
};

export default AppHeader;
